import { z } from 'zod';
import { ArgoMaterialStatusEnum, ArgoWorkOrderStatusEnum } from '../../../../../db';
import {
    type DataSocketKind,
    type NodeSocketCfg,
    baseNodeCfgSchema,
} from './base-node';

export const workOrderSubscribeColumnsSchema = z.object({
    status: z.boolean(),
    orderNumber: z.boolean(),
    noneCount: z.boolean(),
    totalCount: z.boolean(),
    passedCount: z.boolean(),
    failedCount: z.boolean(),
    startedCount: z.boolean(),
    scrappedCount: z.boolean(),
    completedCount: z.boolean(),
    targetQuantity: z.boolean(),
    incompleteCount: z.boolean(),
    /** @note this will not trigger reactive updates to save some cycles, since it's mainly intended for diagnostics anyway */
    rawMaterialData: z.boolean(),
    quarantinedCount: z.boolean(),
});

export type WorkOrderSubscribeColumns = z.infer<typeof workOrderSubscribeColumnsSchema>;
export type WorkOrderNodeOutputs = keyof WorkOrderSubscribeColumns;
export type WorkOrderDataOutputs = {
    [key in WorkOrderNodeOutputs]?: DataSocketKind;
}

/**
 * This is the return type from the API subscription. It extends the work order subscribe columns so if a new column is added it will automatically be added to the return type as never until it is added here
 * @note this is the unconstrained version, and not constrained yet by `WorkOrderSubscribeColumns`
 */
type _WorkOrderDataPayload = {
    status: ArgoWorkOrderStatusEnum;
    orderNumber: string;
    rawMaterialData: {
        id: string;
        status: ArgoMaterialStatusEnum;
    }[];
} & Record<
    'completedCount' |
    'failedCount' |
    'incompleteCount' |
    'noneCount' |
    'passedCount' |
    'quarantinedCount' |
    'scrappedCount' |
    'startedCount' |
    'targetQuantity' |
    'totalCount',
    number
>;
/**
 * @note constrained by `WorkOrderSubscribeColumns`
 */
export type WorkOrderDataPayload = {
    [key in keyof WorkOrderSubscribeColumns]?: _WorkOrderDataPayload[key];
};

export const workOrderCfgSchema = baseNodeCfgSchema.extend({
    controlValues: z.object({
        /** columns on the work order table to subscribe to */
        subcribedColumns: workOrderSubscribeColumnsSchema,
    }),
    kind: z.literal('workOrder'),
});
interface WorkOrderCfgSchema extends z.infer<typeof workOrderCfgSchema> {}

export const workOrderSocketCfg = {
    controlSockets: {
        inputs: () => ({ watchWorkOrder: 'event' }),
        outputs: () => ({ workOrderUpdate: 'event' }),
    },
    dataSockets: {
        inputs: () => ({
            workOrderId: 'string',
        }),
        outputs: (): WorkOrderDataOutputs => ({
            completedCount: 'number',
            failedCount: 'number',
            incompleteCount: 'number',
            noneCount: 'number',
            passedCount: 'number',
            quarantinedCount: 'number',
            rawMaterialData: 'workOrderMaterialData',
            scrappedCount: 'number',
            startedCount: 'number',
            status: 'string',
            targetQuantity: 'number',
            totalCount: 'number',
            orderNumber: 'string',
        }),
    },
} satisfies NodeSocketCfg<WorkOrderCfgSchema>;
